import React, { useCallback, useEffect, useState } from "react";
import { Form, Button, Table, Card, Spinner, Badge, Alert } from "react-bootstrap";
import Layout from "views/universal/Layout/Layout";
import { getFunnel } from "./api";

/**
 * Tableau de suivi de l'A/B test front B2C : funnel comparé v1 (tunnel Twig)
 * vs v2 (app Nuxt) en nombre, pourcentage et CA.
 *
 * Le funnel est CUMULÉ (« a atteint au moins cette marche ») : sessions →
 * création panier → ajout produit → étape de paiement → commande. Il décroît
 * donc toujours. Le détail des sorties (abandons par étape, paniers encore en
 * cours) est donné dans une seconde table : le funnel dit combien arrivent,
 * les sorties disent où ils partent.
 *
 * Deux modes :
 *  - « Période (au réel) » : chaque marche est comptée à l'heure où elle s'est
 *    produite (sessions, paniers à l'heure d'entrée du client, commandes) ;
 *  - « Journée de livraison » : paniers et commandes rattachés à cette date de
 *    livraison (les sessions n'ont pas de date de livraison → masquées).
 *
 * Données : compteurs anonymes store_front_stat + store_stock_cart (paniers en
 * cours) + meta_data (abandons) + store_order.version_front
 * (cf. docs/store/ab-test-front-v2.md côté API).
 */

const VERSION_LABELS = { 1: "Tunnel V1 (site)", 2: "Tunnel V2 (app)" };
const VERSION_VARIANTS = { 1: "secondary", 2: "success" };
const VERSION_COLORS = { 1: "#6c757d", 2: "#429853" };

/**
 * Schéma de funnel : une barre centrée par marche, largeur proportionnelle à
 * la première marche disponible, taux de passage affiché entre deux barres.
 * Pur CSS (pas de lib de chart), même rendu pour les deux versions → les
 * silhouettes se comparent d'un coup d'œil entre les deux cartes.
 */
const FunnelChart = ({ steps, color }) => {
  const kept = steps.filter((s) => s.value != null);
  if (!kept.length) return null;
  const max = Math.max(...kept.map((s) => s.value), 1);

  return (
    <div style={{ margin: "14px 0 6px" }}>
      {kept.map((s, i) => {
        const widthPct = Math.max((s.value / max) * 100, s.value > 0 ? 6 : 2);
        const prev = i > 0 ? kept[i - 1] : null;
        const passage = prev && prev.value ? `${((s.value / prev.value) * 100).toFixed(1).replace(".", ",")} %` : null;
        return (
          <div key={s.label}>
            {passage && (
              <div style={{ textAlign: "center", fontSize: 11, color: "#999", lineHeight: "16px" }}>
                ▼ {passage}
              </div>
            )}
            <div
              style={{
                width: `${widthPct}%`,
                margin: "0 auto",
                background: color,
                opacity: 1 - i * 0.16,
                borderRadius: 4,
                color: "#fff",
                textAlign: "center",
                padding: "5px 2px",
                fontSize: 12,
                fontWeight: 600,
                minWidth: 34,
                whiteSpace: "nowrap",
              }}
              title={s.label}
            >
              {s.value}
            </div>
            <div style={{ textAlign: "center", fontSize: 11, color: "#666", marginBottom: 4 }}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const euros = (cents) =>
  (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const pct = (part, total) => {
  if (!total || part == null) return "—";
  return `${((part / total) * 100).toFixed(1).replace(".", ",")} %`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Test de significativité de l'écart de conversion SESSIONS → commandes entre
 * les deux versions (test z de deux proportions, seuil 95 % : |z| ≥ 1,96).
 * En mode « journée de livraison » (pas de sessions), repli sur les paniers
 * aboutis — `basis` dit quelle base a servi. Garde-fous d'échantillon :
 * ≥ 20 unités de base par version et effectifs attendus ≥ 5 — en dessous, on
 * refuse de conclure.
 */
function significance(v1, v2) {
  const useSessions = v1.visits != null && v2.visits != null && (v1.visits || v2.visits);
  const basis = useSessions ? "sessions" : "paniers";
  const n1 = useSessions ? v1.visits : v1.abandons.total + v1.orders;
  const n2 = useSessions ? v2.visits : v2.abandons.total + v2.orders;
  const x1 = v1.orders;
  const x2 = v2.orders;
  if (!n1 || !n2) return { status: "insufficient", basis };
  const p1 = x1 / n1;
  const p2 = x2 / n2;
  const p = (x1 + x2) / (n1 + n2);
  const expectedOk = [n1 * p, n1 * (1 - p), n2 * p, n2 * (1 - p)].every((e) => e >= 5);
  if (n1 < 20 || n2 < 20 || !expectedOk) return { status: "insufficient", basis, p1, p2 };
  const se = Math.sqrt(p * (1 - p) * (1 / n1 + 1 / n2));
  if (!se) return { status: "insufficient", basis, p1, p2 };
  const z = (p2 - p1) / se;
  if (Math.abs(z) < 1.96) return { status: "ns", basis, p1, p2, z };
  return { status: z > 0 ? "v2" : "v1", basis, p1, p2, z };
}

/**
 * Courbe du taux de conversion quotidien (sessions → commandes) par version.
 * SVG pur : un point par jour ayant au moins une session (les jours vides ne
 * tracent pas de point — la ligne saute par-dessus). Les sessions démarrant en
 * prod le 03/08/2026 (premier jour complet : 04/08), les jours antérieurs
 * n'ont pas de point.
 */
const DailyChart = ({ daily }) => {
  const W = 640;
  const H = 140;
  const PAD = { top: 10, right: 10, bottom: 22, left: 34 };
  const days = daily || [];
  if (days.length < 2) return null;

  const series = { 1: [], 2: [] };
  days.forEach((day) => {
    ["1", "2"].forEach((v) => {
      const s = day.versions[v];
      const n = s.visits || 0;
      series[v].push(n ? (s.orders / n) * 100 : null);
    });
  });
  if (series["1"].every((p) => p == null) && series["2"].every((p) => p == null)) return null;

  const x = (i) => PAD.left + (i * (W - PAD.left - PAD.right)) / (days.length - 1);
  const y = (rate) => PAD.top + ((100 - rate) * (H - PAD.top - PAD.bottom)) / 100;
  const path = (pts) =>
    pts
      .map((r, i) => (r == null ? null : `${x(i).toFixed(1)},${y(r).toFixed(1)}`))
      .filter(Boolean)
      .join(" ");
  const labelEvery = Math.max(1, Math.ceil(days.length / 8));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 900, display: "block" }}>
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(g)} y2={y(g)} stroke="#eee" />
          <text x={PAD.left - 4} y={y(g) + 3} fontSize="9" fill="#999" textAnchor="end">
            {g}%
          </text>
        </g>
      ))}
      {["1", "2"].map((v) => (
        <g key={v}>
          <polyline points={path(series[v])} fill="none" stroke={VERSION_COLORS[v]} strokeWidth="2" />
          {series[v].map((r, i) =>
            r == null ? null : (
              <circle key={i} cx={x(i)} cy={y(r)} r="3" fill={VERSION_COLORS[v]}>
                <title>{`${days[i].date} — ${VERSION_LABELS[v]} : ${r.toFixed(1)} % (${day_orders(days[i], v)})`}</title>
              </circle>
            )
          )}
        </g>
      ))}
      {days.map((day, i) =>
        i % labelEvery === 0 || i === days.length - 1 ? (
          <text key={day.date} x={x(i)} y={H - 8} fontSize="9" fill="#999" textAnchor="middle">
            {day.date.slice(8)}/{day.date.slice(5, 7)}
          </text>
        ) : null
      )}
    </svg>
  );
};

const day_orders = (day, v) => {
  const s = day.versions[v];
  return `${s.orders} commande${s.orders > 1 ? "s" : ""} / ${s.visits || 0} sessions`;
};

const AbTest = (props) => {
  const [mode, setMode] = useState("period");
  // Par défaut : la journée en cours (élargir la période à la main au besoin).
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [deliveryDate, setDeliveryDate] = useState(todayStr());
  // Créneau horaire quotidien optionnel (vide = journée entière) : appliqué à
  // chaque jour de la période, sur l'heure d'action du client.
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = mode === "period" ? { from, to } : { deliveryDate };
    if (timeFrom) params.timeFrom = timeFrom;
    if (timeTo) params.timeTo = timeTo;
    getFunnel(params)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.result?.message || err?.message || "Échec du chargement.");
        setLoading(false);
      });
  }, [mode, from, to, deliveryDate, timeFrom, timeTo]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const versions = data?.versions || null;
  const isDelivery = data?.mode === "deliveryDate";

  /**
   * Marches cumulées : chaque ligne compte les paniers ayant atteint AU MOINS
   * cette étape, tous sorts confondus (encore en cours, abandonnés, convertis).
   * La suite est donc décroissante. Deux taux : part de la première marche
   * (sessions, ou paniers en mode livraison) et passage depuis la précédente —
   * c'est le second qui désigne la marche qui casse.
   */
  const funnelRows = (v) => {
    if (!v) return [];
    const rows = [];

    if (!isDelivery) {
      rows.push({ label: "Sessions", value: v.visits });
    }
    rows.push({
      label: "Création panier",
      value: v.reached.cart,
      hint: "Le panier naît à la validation du point de retrait.",
    });
    rows.push({
      label: "Ajout produit",
      value: v.reached.product,
      hint: "Au moins un produit ou menu ajouté au panier.",
    });
    rows.push({
      label: "Étape de paiement",
      value: v.reached.payment,
      hint:
        "Commande créée en attente de paiement — V1 : clic sur Payer, V2 : ouverture de la popup de paiement. " +
        "Seul marqueur posé au même instant par les deux tunnels, donc le seul comparable.",
    });
    rows.push({ label: "Commande passée", value: v.orders, strong: true });

    // Taux calculés une fois les valeurs connues : base = première marche.
    const base = rows[0].value;
    return rows.map((r, i) => ({
      ...r,
      base: i === 0 ? null : base,
      prev: i === 0 ? null : rows[i - 1].value,
    }));
  };

  /**
   * Sorties du tunnel, en regard du funnel : où s'arrêtent ceux qui ne
   * commandent pas. Les paniers encore en cours n'ont pas tranché — ils sont
   * comptés dans les marches mais exclus du taux de conversion.
   */
  const exitRows = (v) => {
    if (!v) return [];
    const started = v.abandons.total + v.orders;
    return [
      { label: "Abandonnés après avoir choisi un point (rien ajouté)", value: v.abandons.delivery, base: started },
      { label: "Abandonnés à la composition", value: v.abandons.product, base: started },
      { label: "Abandonnés paiement engagé", value: v.abandons.payment, base: started },
      { label: "Paniers encore en cours (sort non tranché)", value: v.live, base: null },
    ];
  };

  // Les deux définitions de la conversion, affichées côte à côte : la « macro »
  // (sessions → commandes — base du bandeau de significativité et de la courbe
  // quotidienne, mode période seulement) et celle du tunnel après entrée
  // (paniers aboutis → commandes, repli du bandeau en mode livraison).
  const conversions = (v) => ({
    sessions: !isDelivery && v.visits ? pct(v.orders, v.visits) : null,
    paniers: pct(v.orders, v.abandons.total + v.orders),
  });

  return (
    <Layout location={props.location?.search}>
      <h3 style={{ color: "#1d1d1b" }}>Suivi A/B test — Tunnel V1 vs V2</h3>

      <Card className="mb-4" style={{ marginTop: 16 }}>
        <Card.Body>
          <Form>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Form.Group style={{ minWidth: 220 }}>
                <Form.Label>Mode d'analyse</Form.Label>
                <Form.Control as="select" value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="period">Période (au réel)</option>
                  <option value="delivery">Journée de livraison</option>
                </Form.Control>
              </Form.Group>

              {mode === "period" ? (
                <>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Du</Form.Label>
                    <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </Form.Group>
                  <Form.Group style={{ minWidth: 160 }}>
                    <Form.Label>Au</Form.Label>
                    <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                  </Form.Group>
                </>
              ) : (
                <Form.Group style={{ minWidth: 160 }}>
                  <Form.Label>Date de livraison</Form.Label>
                  <Form.Control
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                  />
                </Form.Group>
              )}

              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>Créneau de</Form.Label>
                <Form.Control type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} />
              </Form.Group>
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>à</Form.Label>
                <Form.Control type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} />
              </Form.Group>

              <Button onClick={load} disabled={loading} style={{ height: 38 }}>
                {loading ? <Spinner animation="border" size="sm" /> : "Afficher"}
              </Button>
              {(timeFrom || timeTo) && (
                <Button
                  variant="outline-secondary"
                  style={{ height: 38 }}
                  onClick={() => {
                    setTimeFrom("");
                    setTimeTo("");
                  }}
                >
                  Journée entière
                </Button>
              )}
            </div>
          </Form>
          {mode === "delivery" && (
            <div style={{ marginTop: 8, color: "#888", fontSize: 13 }}>
              Les sessions n'ont pas de date de livraison : ce mode compare uniquement les paniers
              et les commandes rattachés au jour de livraison choisi.
            </div>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {data?.timeWindow && (
        <Alert variant="info" style={{ padding: "8px 12px" }}>
          ⏱ Créneau horaire appliqué : <b>{data.timeWindow.from} → {data.timeWindow.to}</b> chaque jour —
          toutes les marches (dont commandes et CA) sont restreintes à cette tranche.
        </Alert>
      )}

      {/* Période à cheval sur une rupture de série (cf. Historique du test en
          bas de page) : les chiffres avant/après ne mesurent pas la même chose. */}
      {!isDelivery && data?.history && data?.from && (() => {
        const fromDay = data.from.slice(0, 10);
        const toDay = (data.to || data.from).slice(0, 10);
        const straddled = data.history.filter((h) => h.rupture && fromDay < h.date && toDay >= h.date);
        if (!straddled.length) return null;
        return (
          <Alert variant="warning" style={{ padding: "8px 12px" }}>
            ⚠️ La période chevauche une rupture de série :{" "}
            {straddled.map((h) => `${h.date.split("-").reverse().join("/")} (${h.title})`).join(", ")} — les
            chiffres avant/après ne sont pas comparables, resserrer la période (détail en bas de page).
          </Alert>
        );
      })()}

      {/* Un funnel cumulé décroît toujours : s'il remonte, c'est que la période
          déborde du démarrage des compteurs de sessions (03/08/2026 à 15h55,
          premier jour complet le 04/08) — les commandes, elles, remontent à
          tout l'historique. */}
      {versions && !isDelivery &&
        ["1", "2"].some((n) => versions[n].reached.cart > (versions[n].visits || 0)) && (
          <Alert variant="warning" style={{ padding: "8px 12px" }}>
            ⚠️ Plus de paniers que de sessions : la période choisie est antérieure au démarrage des
            compteurs de sessions (04/08/2026). Les marches amont sont incomplètes — comparer les
            marches basses (paniers → paiement → commandes) ou resserrer la période.
          </Alert>
        )}

      {versions && (() => {
        const sig = significance(versions["1"], versions["2"]);
        const fmt = (p) => (p != null ? `${(p * 100).toFixed(1).replace(".", ",")} %` : "—");
        const detail = sig.p1 != null ? ` — conversion ${sig.basis} → commandes : v1 ${fmt(sig.p1)} vs v2 ${fmt(sig.p2)}` : "";
        if (sig.status === "insufficient") {
          return <Alert variant="secondary">📊 Échantillon insuffisant pour conclure (min. 20 {sig.basis} par version){detail}.</Alert>;
        }
        if (sig.status === "ns") {
          return <Alert variant="warning">⚖️ Pas d'écart significatif entre les deux tunnels à ce stade (seuil 95 %){detail}. Continuer à collecter.</Alert>;
        }
        return (
          <Alert variant={sig.status === "v2" ? "success" : "danger"}>
            {sig.status === "v2" ? "✅" : "⚠️"} Écart <b>significatif</b> (95 %) en faveur du{" "}
            <b>{VERSION_LABELS[sig.status === "v2" ? 2 : 1]}</b>
            {detail}.
          </Alert>
        );
      })()}

      {!isDelivery && data?.daily && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title style={{ fontSize: 15 }}>
              Conversion quotidienne (sessions → commandes)
              <span style={{ marginLeft: 12, fontSize: 12, color: "#888" }}>
                <span style={{ color: VERSION_COLORS[1] }}>●</span> V1&nbsp;&nbsp;
                <span style={{ color: VERSION_COLORS[2] }}>●</span> V2
              </span>
            </Card.Title>
            <DailyChart daily={data.daily} />
          </Card.Body>
        </Card>
      )}

      {loading && !data ? (
        <Spinner animation="border" />
      ) : (
        versions && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
            {["1", "2"].map((num) => {
              const v = versions[num];
              const rows = funnelRows(v);
              const avg = v.orders ? v.revenueCents / v.orders : null;
              const funnelSteps = rows.map((r) => ({ label: r.label, value: r.value }));
              return (
                <Card key={num} style={{ flex: "1 1 420px" }}>
                  <Card.Body>
                    <Card.Title style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span>
                        <Badge variant={VERSION_VARIANTS[num]}>{VERSION_LABELS[num]}</Badge>
                      </span>
                      <span style={{ fontSize: 13, textAlign: "right", color: "#429853", lineHeight: 1.5 }}>
                        {conversions(v).sessions != null && (
                          <div>
                            Sessions → commandes : <b>{conversions(v).sessions}</b>
                          </div>
                        )}
                        <div>
                          Paniers → commandes : <b>{conversions(v).paniers}</b>
                        </div>
                      </span>
                    </Card.Title>

                    <FunnelChart steps={funnelSteps} color={VERSION_COLORS[num]} />

                    <Table size="sm" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>Marche</th>
                          <th style={{ textAlign: "right" }}>Nombre</th>
                          <th style={{ textAlign: "right" }}>{isDelivery ? "% paniers" : "% sessions"}</th>
                          <th style={{ textAlign: "right" }}>% préc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.label} title={r.hint || undefined}>
                            <td style={{ fontWeight: r.strong ? 600 : undefined }}>{r.label}</td>
                            <td style={{ textAlign: "right", fontWeight: r.strong ? 600 : undefined }}>
                              {r.value == null ? "—" : r.value}
                            </td>
                            <td style={{ textAlign: "right", color: "#888" }}>
                              {r.base != null ? pct(r.value, r.base) : r.value == null ? "—" : "100,0 %"}
                            </td>
                            <td style={{ textAlign: "right", color: "#888" }}>
                              {r.prev != null ? pct(r.value, r.prev) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <div style={{ fontSize: 12, color: "#888", margin: "10px 0 2px" }}>Où sortent-ils ?</div>
                    <Table size="sm" borderless style={{ marginBottom: 0 }}>
                      <tbody>
                        {exitRows(v).map((r) => (
                          <tr key={r.label}>
                            <td style={{ color: "#888", fontSize: 13, paddingLeft: 0 }}>{r.label}</td>
                            <td style={{ textAlign: "right", fontSize: 13 }}>{r.value == null ? "—" : r.value}</td>
                            <td style={{ textAlign: "right", color: "#888", fontSize: 13, width: 70 }}>
                              {r.base != null ? pct(r.value, r.base) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, flexWrap: "wrap", gap: 8 }}>
                      <div>
                        CA TTC : <b>{euros(v.revenueCents)}</b>
                        <span style={{ color: "#888" }}> — HT : {euros(v.revenueHtCents)}</span>
                      </div>
                      <div>
                        Panier moyen TTC : <b>{avg != null ? euros(avg) : "—"}</b>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )
      )}

      {/* Historique du test (constante métier FrontStat::abTestHistory, servie
          par l'API) : changements de ratio, re-tirages, évolutions de la mesure.
          Les entrées « rupture » interdisent les comparaisons à cheval. */}
      {data?.history?.length > 0 && (
        <Card className="mb-3" style={{ marginTop: 16 }}>
          <Card.Body>
            <Card.Title style={{ fontSize: 15 }}>Historique du test</Card.Title>
            <Table size="sm" borderless style={{ marginBottom: 0 }}>
              <tbody>
                {data.history.map((h) => (
                  <tr key={`${h.date}-${h.title}`}>
                    <td style={{ whiteSpace: "nowrap", width: 100, paddingLeft: 0, fontWeight: 600 }}>
                      {h.date.split("-").reverse().join("/")}
                    </td>
                    <td style={{ width: 280 }}>
                      {h.rupture && (
                        <Badge variant="warning" style={{ marginRight: 6 }}>
                          Rupture
                        </Badge>
                      )}
                      <b>{h.title}</b>
                    </td>
                    <td style={{ color: "#888", fontSize: 13 }}>{h.detail}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div style={{ marginTop: 8, color: "#888", fontSize: 12 }}>
              ⚠️ « Rupture » : la mesure ou la population change à cette date — ne pas comparer une
              période à cheval dessus.
            </div>
          </Card.Body>
        </Card>
      )}

      <div style={{ marginTop: 16, color: "#888", fontSize: 13 }}>
        Les compteurs (sessions, paniers, abandons horodatés) démarrent au déploiement prod du
        04/08/2026 (03/08 après-midi) — les commandes et leur CA sont complets sur tout
        l'historique de l'A/B test. Les abandons excluent les paniers purgés après conversion ;
        les bots connus ne sont pas comptés dans les sessions. Une session = une arrivée sur la
        carte ou le récapitulatif, une par visiteur et par heure — les pages compte et connexion
        n'en comptent pas.
      </div>
    </Layout>
  );
};

export default AbTest;
